import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, useLoadScript, InfoWindow, Circle, Polyline, OverlayView, useGoogleMap } from '@react-google-maps/api';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Slider,
  FormControlLabel,
  Switch,
  Collapse,
  Divider,
  IconButton,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import GroupIcon from '@mui/icons-material/Group';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import WifiIcon from '@mui/icons-material/Wifi';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useSnackbar } from 'notistack';
import { formatLatencyMs } from '../services/latency';
import {
  buildCapDeviceTreeIdsForGroup,
  hasPublicWifi,
  has5GHzPublicWifi,
  getRrmMapMarkerStyle as computeRrmMapMarkerStyle,
  showApiWarnings,
} from '../utils/publicWifiRrmPlanUi';
import { api } from '../services/api';
import { formatRadioProfileOptionLabel } from '../utils/radioProfileBand';
import { formatSsidProfileOptionLabel } from '../utils/ssidProfileLabel';
import {
  MAP_UI,
  publicWifiStatSx,
  publicWifiMapActionBtnSx,
  rrmPlanChipSx,
  healthChipSoftSx,
} from '../theme/mapViewUi';

const GOOGLE_MAPS_API_KEY = 'AIzaSyDBzNIMvmobitJ-vi8Z-kzVfK99qSb7jRw';
const GOOGLE_MAPS_LIBRARIES = ['geometry', 'marker'];

const defaultCenter = {
  lat: 32.0853, // Tel Aviv, Israel (default center)
  lng: 34.7818
};

// Custom AdvancedMarker component using native Google Maps AdvancedMarkerElement
const AdvancedMarker = ({ position, content, onClick, title, zIndex }) => {
  const markerRef = React.useRef(null);
  const map = useGoogleMap();

  React.useEffect(() => {
    if (!window.google?.maps?.marker?.AdvancedMarkerElement || !map) {
      return;
    }

    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      position,
      content,
      title,
      map,
      zIndex,
    });

    if (onClick) {
      marker.addListener('click', onClick);
    }

    markerRef.current = marker;

    return () => {
      if (markerRef.current) {
        markerRef.current.map = null;
      }
    };
  }, [position, content, onClick, title, zIndex, map]);

  return null;
};

const PublicWifiExpansion = ({ devices = [], isActive = true, readOnly = false }) => {
  const canMutate = !readOnly;
  const navigate = useNavigate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [coverageRadius, setCoverageRadius] = useState(30);
  const [useIperfBackhaul, setUseIperfBackhaul] = useState(true);
  const [iperfDirection, setIperfDirection] = useState('both');
  const [requireIperf, setRequireIperf] = useState(true);
  const [hideLowScore, setHideLowScore] = useState(true);
  const [minBackhaulUpMbps, setMinBackhaulUpMbps] = useState('');
  const [minBackhaulDownMbps, setMinBackhaulDownMbps] = useState('');
  const [iperfOptionsExpanded, setIperfOptionsExpanded] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [showConnections, setShowConnections] = useState(false);
  const selectionDefaultsKeyRef = useRef('');
  const [deploymentPlanMode, setDeploymentPlanMode] = useState(false);
  const [rrmData, setRrmData] = useState(null);
  const [rrmLoading, setRrmLoading] = useState(false);
  const [rrmError, setRrmError] = useState(null);
  const mapRef = useRef(null);
  const mapViewportRef = useRef({ center: defaultCenter, zoom: 12 });
  const hadMapSelectionRef = useRef(false);

  // Selection & action state for Planned APs / Enrichment tables
  const [selectedPlannedSerials, setSelectedPlannedSerials] = useState(new Set());
  const [selectedEnrichmentSerials, setSelectedEnrichmentSerials] = useState(new Set());
  const [plannedTableSortCol, setPlannedTableSortCol] = useState('');
  const [plannedTableSortDir, setPlannedTableSortDir] = useState('desc');
  const [enrichmentTableSortCol, setEnrichmentTableSortCol] = useState('');
  const [enrichmentTableSortDir, setEnrichmentTableSortDir] = useState('desc');
  const [ssidProfiles, setSsidProfiles] = useState([]);
  const [vlanProfiles, setVlanProfiles] = useState([]);
  const [radioProfiles, setRadioProfiles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionDialogSource, setActionDialogSource] = useState('planned');
  const [actionMode, setActionMode] = useState('create_group');
  const [actionGroupName, setActionGroupName] = useState('');
  const [actionGroupId, setActionGroupId] = useState('');
  const [actionSsidId, setActionSsidId] = useState('');
  const [actionRadioProfileId, setActionRadioProfileId] = useState('');
  const [actionSaving, setActionSaving] = useState(false);
  // Menu anchor for the "Group" split button
  const [groupMenuAnchor, setGroupMenuAnchor] = useState(null);
  const [groupMenuSource, setGroupMenuSource] = useState('planned');

  // Load Google Maps
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const mapColorScheme = React.useMemo(() => {
    if (!isLoaded || !window.google?.maps?.ColorScheme) {
      return undefined;
    }
    return theme.palette.mode === 'dark'
      ? window.google.maps.ColorScheme.DARK
      : window.google.maps.ColorScheme.LIGHT;
  }, [isLoaded, theme.palette.mode]);

  React.useEffect(() => {
    if (!mapRef.current || !mapColorScheme) {
      return;
    }
    mapRef.current.setOptions({ colorScheme: mapColorScheme });
  }, [mapColorScheme]);

  // Serial → {lat, lng, device} for ALL devices (including CAPs) — used for connection lines.
  const gpsLookup = useMemo(() => {
    const map = {};
    if (!devices || !Array.isArray(devices)) return map;
    devices.forEach(d => {
      const serial = (d.serial_number || '').toLowerCase();
      if (!serial) return;
      const gps = d?.gps || null;
      if (!gps || gps.lat == null || gps.lng == null) return;
      const lat = typeof gps.lat === 'number' ? gps.lat : parseFloat(gps.lat);
      const lng = typeof gps.lng === 'number' ? gps.lng : parseFloat(gps.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return;
      map[serial] = { lat, lng, device: d };
    });
    return map;
  }, [devices]);

  // Parent→child connection lines, computed only when showConnections is on.
  const connectionLines = useMemo(() => {
    if (!showConnections || !devices || !Array.isArray(devices)) return [];
    const lines = [];
    devices.forEach(d => {
      if (!d.parent_serial) return;
      const serial = (d.serial_number || '').toLowerCase();
      const parentSerial = d.parent_serial.toLowerCase();
      const me = gpsLookup[serial];
      const par = gpsLookup[parentSerial];
      if (!me || !par) return;

      // Haversine distance in metres
      const R = 6371000;
      const dLat = (par.lat - me.lat) * Math.PI / 180;
      const dLng = (par.lng - me.lng) * Math.PI / 180;
      const a = Math.sin(dLat / 2) ** 2
        + Math.cos(me.lat * Math.PI / 180) * Math.cos(par.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
      const distance = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      if (distance === 0) return;

      // RSSI: look in parent's private/public wifi associations for this device
      const deviceMacs = (d.radios || []).map(r => r.mac?.toLowerCase()).filter(Boolean);
      const deviceIds = new Set([serial, serial.replace(/:/g, ''), ...deviceMacs]);
      const searchInterfaces = (interfaces) => {
        if (!interfaces) return null;
        for (const iface of interfaces) {
          const assocs = iface.Associations || iface.associations;
          if (!assocs) continue;
          for (const assoc of assocs) {
            const station = (assoc.Station || assoc.station || '').toLowerCase();
            if (deviceIds.has(station) || deviceIds.has(station.replace(/:/g, ''))) {
              const rssi = assoc.RSSI || assoc.rssi;
              return rssi != null ? parseInt(rssi, 10) : null;
            }
          }
        }
        return null;
      };
      const rssi = searchInterfaces(par.device.private_wifi?.interfaces || par.device.private_wifi?.Interfaces)
                ?? searchInterfaces(par.device.public_wifi?.interfaces  || par.device.public_wifi?.Interfaces);

      const color = rssi != null
        ? (rssi >= -85 ? MAP_UI.throughputExcellent : MAP_UI.throughputPoor)
        : MAP_UI.rrmPlanMarker;
      const distText = distance < 1000 ? `${Math.round(distance)}m` : `${(distance / 1000).toFixed(2)}km`;
      const label = rssi != null ? `${rssi} dBm` : distText;

      lines.push({
        key: `conn-${serial}`,
        from: me,
        to: par,
        color,
        label,
        midLat: (me.lat + par.lat) / 2,
        midLng: (me.lng + par.lng) / 2,
        rssi,
        distText,
      });
    });
    return lines;
  }, [showConnections, devices, gpsLookup]);

  // All NODE devices with Public WiFi (includes offline — for markers and stats)
  const allDevicesWithPublicWifi = useMemo(() => {
    if (!devices || !Array.isArray(devices)) return [];
    return devices.filter(device => hasPublicWifi(device));
  }, [devices]);

  // Set of lowercase serials that currently have any Public WiFi enabled (any band).
  const publicWifiAnySerials = useMemo(() => {
    return new Set(
      allDevicesWithPublicWifi
        .map(d => (d.serial_number || d.SerialNumber || '').toLowerCase())
        .filter(Boolean)
    );
  }, [allDevicesWithPublicWifi]);

  // Set of lowercase serials that currently have an active 5 GHz Public WiFi AP interface.
  const publicWifi5GHzSerials = useMemo(() => {
    if (!devices || !Array.isArray(devices)) return new Set();
    return new Set(
      devices
        .filter(d => has5GHzPublicWifi(d))
        .map(d => (d.serial_number || d.SerialNumber || '').toLowerCase())
        .filter(Boolean)
    );
  }, [devices]);

  // O(1) lookup from serial → candidate entry
  const candidateMap = useMemo(() => {
    if (!rrmData?.candidates) return {};
    return Object.fromEntries(rrmData.candidates.map(c => [c.serial, c]));
  }, [rrmData]);

  // O(1) lookup from serial → plan entry (lowercase keys for case-safe lookup)
  const planEntryMap = useMemo(() => {
    if (!rrmData?.plan_5ghz) return {};
    return Object.fromEntries(rrmData.plan_5ghz.map(e => [(e.serial || '').toLowerCase(), e]));
  }, [rrmData]);

  // Set of serials covered by the deployment plan
  const coveredSerialSet = useMemo(() => {
    return new Set((rrmData?.plan_5ghz || []).flatMap(e => e.covered_serials || []));
  }, [rrmData]);

  // Excluded critical parents that CURRENTLY have 5 GHz Public WiFi — operational conflict.
  const conflictingExcluded = useMemo(() => {
    if (!rrmData?.excluded) return [];
    return rrmData.excluded.filter(ex => publicWifi5GHzSerials.has((ex.serial || '').toLowerCase()));
  }, [rrmData, publicWifi5GHzSerials]);

  // Candidates to show in the recommendations table
  const tableRecommendations = useMemo(() => {
    const cands = rrmData?.candidates || [];
    const filtered = hideLowScore
      ? cands.filter(c => c.recommendation !== 'not_recommended')
      : cands;
    return [...filtered].sort((a, b) => b.score_5ghz - a.score_5ghz);
  }, [rrmData, hideLowScore]);

  /** Minimal plan AP rows (backend order — area-covering set). Not filtered by hideLowScore. */
  const plannedApRows = useMemo(() => {
    if (!rrmData?.plan_5ghz?.length) return [];
    return rrmData.plan_5ghz.map((pe) => ({
      planEntry: pe,
      cand: candidateMap[pe.serial] ?? null,
    }));
  }, [rrmData, candidateMap]);

  /** Scored candidates not selected in the minimal plan — optional extra APs for denser coverage. */
  const enrichmentCandidates = useMemo(() => {
    return tableRecommendations.filter(
      (c) => !planEntryMap[(c.serial || '').toLowerCase()],
    );
  }, [tableRecommendations, planEntryMap]);

  const backhaulFilterActive = useMemo(() => {
    const up = Number(minBackhaulUpMbps);
    const down = Number(minBackhaulDownMbps);
    return (minBackhaulUpMbps !== '' && !isNaN(up) && up > 0) ||
           (minBackhaulDownMbps !== '' && !isNaN(down) && down > 0);
  }, [minBackhaulUpMbps, minBackhaulDownMbps]);

  const iperfDirectionLabel = useMemo(() => {
    if (iperfDirection === 'uplink') return 'uplink';
    if (iperfDirection === 'downlink') return 'downlink';
    return 'avg up/down';
  }, [iperfDirection]);

  // Returns { upMbps, downMbps } from the RRM plan candidate's newest iperf
  // all_to_cap measurements (device→CAP up, CAP→device down).
  const getCandidateIperfMbps = useCallback((serial) => {
    const cand = candidateMap[serial];
    const up = cand?.iperf_up_mbps;
    const down = cand?.iperf_down_mbps;
    return {
      upMbps: typeof up === 'number' && Number.isFinite(up) ? up : null,
      downMbps: typeof down === 'number' && Number.isFinite(down) ? down : null,
    };
  }, [candidateMap]);

  // Returns true when a device passes the active backhaul filter thresholds.
  const passesBackhaulFilter = useCallback((serial) => {
    if (!backhaulFilterActive) return true;
    const { upMbps, downMbps } = getCandidateIperfMbps(serial);
    const minUp   = Number(minBackhaulUpMbps);
    const minDown = Number(minBackhaulDownMbps);
    if (minBackhaulUpMbps !== '' && !isNaN(minUp) && minUp > 0) {
      if (upMbps == null || upMbps < minUp) return false;
    }
    if (minBackhaulDownMbps !== '' && !isNaN(minDown) && minDown > 0) {
      if (downMbps == null || downMbps < minDown) return false;
    }
    return true;
  }, [getCandidateIperfMbps, minBackhaulUpMbps, minBackhaulDownMbps, backhaulFilterActive]);

  // Planned AP rows after applying the backhaul speed filter.
  const filteredPlannedApRows = useMemo(() => {
    if (!backhaulFilterActive) return plannedApRows;
    return plannedApRows.filter(r => passesBackhaulFilter(r.planEntry.serial));
  }, [plannedApRows, passesBackhaulFilter, backhaulFilterActive]);

  // Enrichment candidates after applying the backhaul speed filter.
  const filteredEnrichmentCandidates = useMemo(() => {
    if (!backhaulFilterActive) return enrichmentCandidates;
    return enrichmentCandidates.filter(c => passesBackhaulFilter(c.serial));
  }, [enrichmentCandidates, passesBackhaulFilter, backhaulFilterActive]);

  const togglePlannedTableSort = useCallback((col) => {
    setPlannedTableSortCol((prevCol) => {
      if (prevCol === col) {
        setPlannedTableSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prevCol;
      }
      setPlannedTableSortDir('desc');
      return col;
    });
  }, []);

  const toggleEnrichmentTableSort = useCallback((col) => {
    setEnrichmentTableSortCol((prevCol) => {
      if (prevCol === col) {
        setEnrichmentTableSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prevCol;
      }
      setEnrichmentTableSortDir('desc');
      return col;
    });
  }, []);

  const sortByNumericCol = useCallback((items, col, dir, getValue) => {
    if (!col) return items;
    const factor = dir === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * factor;
    });
  }, []);

  const sortedFilteredPlannedApRows = useMemo(
    () => sortByNumericCol(
      filteredPlannedApRows,
      plannedTableSortCol,
      plannedTableSortDir,
      (row) => {
        if (plannedTableSortCol === 'score') return row.cand?.score_5ghz ?? null;
        if (plannedTableSortCol === 'backhaul_score') return row.cand?.backhaul_score ?? null;
        if (plannedTableSortCol === 'up_mbps') return row.cand?.iperf_up_mbps ?? null;
        if (plannedTableSortCol === 'down_mbps') return row.cand?.iperf_down_mbps ?? null;
        return null;
      },
    ),
    [filteredPlannedApRows, plannedTableSortCol, plannedTableSortDir, sortByNumericCol],
  );

  const sortedFilteredEnrichmentCandidates = useMemo(
    () => sortByNumericCol(
      filteredEnrichmentCandidates,
      enrichmentTableSortCol,
      enrichmentTableSortDir,
      (cand) => {
        if (enrichmentTableSortCol === 'score') return cand.score_5ghz ?? null;
        if (enrichmentTableSortCol === 'backhaul_score') return cand.backhaul_score ?? null;
        if (enrichmentTableSortCol === 'up_mbps') return cand.iperf_up_mbps ?? null;
        if (enrichmentTableSortCol === 'down_mbps') return cand.iperf_down_mbps ?? null;
        return null;
      },
    ),
    [filteredEnrichmentCandidates, enrichmentTableSortCol, enrichmentTableSortDir, sortByNumericCol],
  );

  // Map shows only table-checked devices: planned APs (default all checked) + enrichment (default none).
  const mapSelectedPlannedEntries = useMemo(
    () => filteredPlannedApRows
      .filter((r) => selectedPlannedSerials.has(r.planEntry.serial))
      .map((r) => r.planEntry),
    [filteredPlannedApRows, selectedPlannedSerials],
  );

  const mapSelectedEnrichmentCandidates = useMemo(
    () => filteredEnrichmentCandidates.filter((c) => selectedEnrichmentSerials.has(c.serial)),
    [filteredEnrichmentCandidates, selectedEnrichmentSerials],
  );

  // Selected enrichment candidates are styled as in-plan APs on the map.
  const mapPlanEntryMapForStyle = useMemo(
    () => Object.fromEntries([
      ...mapSelectedPlannedEntries.map((e) => [(e.serial || '').toLowerCase(), e]),
      ...mapSelectedEnrichmentCandidates.map((c) => [(c.serial || '').toLowerCase(), c]),
    ]),
    [mapSelectedPlannedEntries, mapSelectedEnrichmentCandidates],
  );

  const iperfPlanOptions = useMemo(() => ({
    useIperfBackhaul,
    iperfDirection,
    requireIperf,
  }), [useIperfBackhaul, iperfDirection, requireIperf]);

  // Call POST /public-wifi/rrm-plan, store result, enter plan mode
  const generatePlan = useCallback(async (radius, options = iperfPlanOptions) => {
    setRrmLoading(true);
    setRrmError(null);
    try {
      const data = await api.runPublicWifiRRMPlan(radius, options);
      setRrmData(data);
      setDeploymentPlanMode(true);
    } catch (err) {
      setRrmError(err?.message || 'Failed to generate RRM plan');
    } finally {
      setRrmLoading(false);
    }
  }, [iperfPlanOptions]);

  // Load last stored plan when the tab becomes active (404 = no plan yet, not an error)
  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;
    api.getPublicWifiRRMPlan()
      .then(data => {
        if (cancelled) return;
        if (data) {
          setRrmData(data);
          if (data.coverage_radius_m > 0) setCoverageRadius(data.coverage_radius_m);
          if (data.iperf_backhaul) {
            setUseIperfBackhaul(!!data.iperf_backhaul.enabled);
            if (data.iperf_backhaul.direction) {
              setIperfDirection(data.iperf_backhaul.direction);
            }
            setRequireIperf(!!data.iperf_backhaul.require_iperf);
          }
          if (data.plan_5ghz?.length > 0) {
            setDeploymentPlanMode(true);
          }
        }
      })
      .catch(err => {
        if (!cancelled && err?.statusCode !== 404) {
          setRrmError(err?.message || 'Failed to load RRM plan');
        }
      });
    return () => { cancelled = true; };
  }, [isActive]);

  // Default selections when plan or list filters change: all planned APs checked, enrichment unchecked.
  useEffect(() => {
    if (!rrmData?.plan_5ghz?.length) {
      selectionDefaultsKeyRef.current = '';
      setSelectedPlannedSerials(new Set());
      setSelectedEnrichmentSerials(new Set());
      return;
    }
    const defaultsKey = [
      rrmData.computed_at,
      rrmData.plan_5ghz.length,
      minBackhaulUpMbps,
      minBackhaulDownMbps,
      hideLowScore,
      filteredPlannedApRows.map((r) => r.planEntry.serial).join(','),
    ].join('|');
    if (selectionDefaultsKeyRef.current === defaultsKey) return;
    selectionDefaultsKeyRef.current = defaultsKey;
    setSelectedPlannedSerials(new Set(filteredPlannedApRows.map((r) => r.planEntry.serial)));
    setSelectedEnrichmentSerials(new Set());
  }, [rrmData, filteredPlannedApRows, minBackhaulUpMbps, minBackhaulDownMbps, hideLowScore]);

  // Fetch SSID profiles, radio profiles and groups once when component mounts.
  useEffect(() => {
    let cancelled = false;
    api.getSSIDProfiles()
      .then(data => { if (!cancelled) setSsidProfiles(data?.ssids || []); })
      .catch(() => { if (!cancelled) setSsidProfiles([]); });
    api.getVLANProfiles()
      .then(data => { if (!cancelled) setVlanProfiles(data?.vlans || []); })
      .catch(() => { if (!cancelled) setVlanProfiles([]); });
    api.getRadioProfiles()
      .then(data => { if (!cancelled) setRadioProfiles(data?.radio_profiles || []); })
      .catch(() => { if (!cancelled) setRadioProfiles([]); });
    api.getGroups()
      .then(data => { if (!cancelled) setGroups(data?.groups || []); })
      .catch(() => { if (!cancelled) setGroups([]); });
    return () => { cancelled = true; };
  }, []);

  // Selection helpers for Planned APs table
  const togglePlannedSerial = useCallback((serial) => {
    setSelectedPlannedSerials(prev => {
      const next = new Set(prev);
      if (next.has(serial)) next.delete(serial); else next.add(serial);
      return next;
    });
  }, []);

  const toggleAllPlanned = useCallback(() => {
    setSelectedPlannedSerials(prev => {
      const allSerials = filteredPlannedApRows.map(r => r.planEntry.serial);
      return prev.size === allSerials.length ? new Set() : new Set(allSerials);
    });
  }, [filteredPlannedApRows]);

  // Selection helpers for Enrichment table
  const toggleEnrichmentSerial = useCallback((serial) => {
    setSelectedEnrichmentSerials(prev => {
      const next = new Set(prev);
      if (next.has(serial)) next.delete(serial); else next.add(serial);
      return next;
    });
  }, []);

  const toggleAllEnrichment = useCallback(() => {
    setSelectedEnrichmentSerials(prev => {
      const allSerials = filteredEnrichmentCandidates.map(c => c.serial);
      return prev.size === allSerials.length ? new Set() : new Set(allSerials);
    });
  }, [filteredEnrichmentCandidates]);

  const handleUnselectMapDevice = useCallback(() => {
    if (!selectedDevice) return;
    const serial = selectedDevice.device?.serial || selectedDevice.device?.serial_number;
    if (!serial) return;
    if (selectedDevice.type === 'selectedAP') {
      setSelectedPlannedSerials((prev) => {
        const next = new Set(prev);
        next.delete(serial);
        return next;
      });
    } else if (selectedDevice.type === 'candidate' || selectedDevice.type === 'node') {
      setSelectedEnrichmentSerials((prev) => {
        const next = new Set(prev);
        next.delete(serial);
        return next;
      });
    }
    setSelectedDevice(null);
  }, [selectedDevice]);

  // Open the action dialog for a given source and mode
  const openActionDialog = useCallback((source, mode) => {
    if (!canMutate) return;
    setActionDialogSource(source);
    setActionMode(mode);
    setActionGroupName('');
    setActionGroupId('');
    setActionSsidId('');
    setActionRadioProfileId('');
    setGroupMenuAnchor(null);
    setActionDialogOpen(true);
  }, [canMutate]);

  /** Shared thead for normal-mode RRM tables (planned APs). */
  const rrmNormalModeTableHead = useMemo(() => {
    const allPlannedSerials = filteredPlannedApRows.map(r => r.planEntry.serial);
    const allChecked = allPlannedSerials.length > 0 && selectedPlannedSerials.size === allPlannedSerials.length;
    const someChecked = selectedPlannedSerials.size > 0 && !allChecked;
    return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox size="small" checked={allChecked} indeterminate={someChecked} onChange={toggleAllPlanned} />
        </TableCell>
        <TableCell sx={{ width: 24 }} />
        <TableCell>
          <Tooltip title="Device serial number" placement="top">
            <span>Serial</span>
          </Tooltip>
        </TableCell>
        <TableCell align="right">
          <Tooltip title="Number of eligible NODEs within this AP's coverage radius in the minimal plan" placement="top">
            <span>NODEs Covered</span>
          </Tooltip>
        </TableCell>
        <TableCell align="right" sortDirection={plannedTableSortCol === 'score' ? plannedTableSortDir : false}>
          <Tooltip title="Overall 5 GHz deployment score (0–100): combines topology safety, backhaul health, and geographic isolation bonus" placement="top">
            <TableSortLabel
              active={plannedTableSortCol === 'score'}
              direction={plannedTableSortCol === 'score' ? plannedTableSortDir : 'asc'}
              onClick={() => togglePlannedTableSort('score')}
            >
              Score
            </TableSortLabel>
          </Tooltip>
        </TableCell>
        <TableCell align="center" colSpan={5} sx={{ borderBottom: '2px solid', borderColor: 'divider' }}>
          <Tooltip title="Backhaul health metrics measured on the device's wireless uplink to its parent" placement="top">
            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
              Backhaul Health
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Tooltip title="Deployment recommendation based on score: Excellent ≥85, Good ≥70, Possible ≥55, Not recommended <55" placement="top">
            <span>Recommendation</span>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ pt: 0, pb: 0.5 }} />
        <TableCell sx={{ pt: 0, pb: 0.5 }} />
        <TableCell sx={{ pt: 0, pb: 0.5 }} />
        <TableCell sx={{ pt: 0, pb: 0.5 }} />
        <TableCell sx={{ pt: 0, pb: 0.5 }} />
        <TableCell align="right" sx={{ pt: 0, pb: 0.5 }}>
          <Tooltip title="Median round-trip latency to parent and CAP. Lower is better." placement="top">
            <Typography variant="caption" color="text.secondary">Latency (ms)</Typography>
          </Tooltip>
        </TableCell>
        <TableCell align="right" sx={{ pt: 0, pb: 0.5 }}>
          <Tooltip title="Fraction of backhaul transmissions that failed. Lower is better." placement="top">
            <Typography variant="caption" color="text.secondary">TX Failed %</Typography>
          </Tooltip>
        </TableCell>
        <TableCell align="right" sortDirection={plannedTableSortCol === 'backhaul_score' ? plannedTableSortDir : false} sx={{ pt: 0, pb: 0.5 }}>
          <Tooltip title="Combined backhaul health score (0–100): 70% latency score + 30% TX success rate" placement="top">
            <TableSortLabel
              active={plannedTableSortCol === 'backhaul_score'}
              direction={plannedTableSortCol === 'backhaul_score' ? plannedTableSortDir : 'asc'}
              onClick={() => togglePlannedTableSort('backhaul_score')}
            >
              <Typography variant="caption" color="text.secondary">Backhaul Score</Typography>
            </TableSortLabel>
          </Tooltip>
        </TableCell>
        <TableCell align="right" sortDirection={plannedTableSortCol === 'up_mbps' ? plannedTableSortDir : false} sx={{ pt: 0, pb: 0.5 }}>
          <Tooltip title="Device → CAP throughput from the newest completed iperf all-to-CAP test (uplink direction)." placement="top">
            <TableSortLabel
              active={plannedTableSortCol === 'up_mbps'}
              direction={plannedTableSortCol === 'up_mbps' ? plannedTableSortDir : 'asc'}
              onClick={() => togglePlannedTableSort('up_mbps')}
            >
              <Typography variant="caption" color="text.secondary">Up (Mbps)</Typography>
            </TableSortLabel>
          </Tooltip>
        </TableCell>
        <TableCell align="right" sortDirection={plannedTableSortCol === 'down_mbps' ? plannedTableSortDir : false} sx={{ pt: 0, pb: 0.5 }}>
          <Tooltip title="CAP → device throughput from the newest completed iperf all-to-CAP test (reverse / downlink direction)." placement="top">
            <TableSortLabel
              active={plannedTableSortCol === 'down_mbps'}
              direction={plannedTableSortCol === 'down_mbps' ? plannedTableSortDir : 'asc'}
              onClick={() => togglePlannedTableSort('down_mbps')}
            >
              <Typography variant="caption" color="text.secondary">Down (Mbps)</Typography>
            </TableSortLabel>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ pt: 0, pb: 0.5 }} />
      </TableRow>
    </TableHead>
    );
  }, [filteredPlannedApRows, selectedPlannedSerials, toggleAllPlanned, plannedTableSortCol, plannedTableSortDir, togglePlannedTableSort]);

  /** Thead for enrichment table — column 3 is distance to minimal plan, not NODEs covered. */
  const rrmEnrichmentTableHead = useMemo(() => {
    const allEnrichSerials = filteredEnrichmentCandidates.map(c => c.serial);
    const allChecked = allEnrichSerials.length > 0 && selectedEnrichmentSerials.size === allEnrichSerials.length;
    const someChecked = selectedEnrichmentSerials.size > 0 && !allChecked;
    return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox size="small" checked={allChecked} indeterminate={someChecked} onChange={toggleAllEnrichment} />
        </TableCell>
        <TableCell sx={{ width: 24 }} />
        <TableCell>
          <Tooltip title="Device serial number" placement="top">
            <span>Serial</span>
          </Tooltip>
        </TableCell>
        <TableCell align="right">
          <Tooltip title="Not in the minimal plan — no coverage count. Approximate distance to the nearest planned AP when the backend provides it." placement="top">
            <span>Nearest plan AP</span>
          </Tooltip>
        </TableCell>
        <TableCell align="right" sortDirection={enrichmentTableSortCol === 'score' ? enrichmentTableSortDir : false}>
          <Tooltip title="Overall 5 GHz deployment score (0–100): combines topology safety, backhaul health, and geographic isolation bonus" placement="top">
            <TableSortLabel
              active={enrichmentTableSortCol === 'score'}
              direction={enrichmentTableSortCol === 'score' ? enrichmentTableSortDir : 'asc'}
              onClick={() => toggleEnrichmentTableSort('score')}
            >
              Score
            </TableSortLabel>
          </Tooltip>
        </TableCell>
        <TableCell align="center" colSpan={5} sx={{ borderBottom: '2px solid', borderColor: 'divider' }}>
          <Tooltip title="Backhaul health metrics measured on the device's wireless uplink to its parent" placement="top">
            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
              Backhaul Health
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Tooltip title="Deployment recommendation based on score: Excellent ≥85, Good ≥70, Possible ≥55, Not recommended <55" placement="top">
            <span>Recommendation</span>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ pt: 0, pb: 0.5 }} />
        <TableCell sx={{ pt: 0, pb: 0.5 }} />
        <TableCell sx={{ pt: 0, pb: 0.5 }} />
        <TableCell sx={{ pt: 0, pb: 0.5 }} />
        <TableCell sx={{ pt: 0, pb: 0.5 }} />
        <TableCell align="right" sx={{ pt: 0, pb: 0.5 }}>
          <Tooltip title="Median round-trip latency to parent and CAP. Lower is better." placement="top">
            <Typography variant="caption" color="text.secondary">Latency (ms)</Typography>
          </Tooltip>
        </TableCell>
        <TableCell align="right" sx={{ pt: 0, pb: 0.5 }}>
          <Tooltip title="Fraction of backhaul transmissions that failed. Lower is better." placement="top">
            <Typography variant="caption" color="text.secondary">TX Failed %</Typography>
          </Tooltip>
        </TableCell>
        <TableCell align="right" sortDirection={enrichmentTableSortCol === 'backhaul_score' ? enrichmentTableSortDir : false} sx={{ pt: 0, pb: 0.5 }}>
          <Tooltip title="Combined backhaul health score (0–100): 70% latency score + 30% TX success rate" placement="top">
            <TableSortLabel
              active={enrichmentTableSortCol === 'backhaul_score'}
              direction={enrichmentTableSortCol === 'backhaul_score' ? enrichmentTableSortDir : 'asc'}
              onClick={() => toggleEnrichmentTableSort('backhaul_score')}
            >
              <Typography variant="caption" color="text.secondary">Backhaul Score</Typography>
            </TableSortLabel>
          </Tooltip>
        </TableCell>
        <TableCell align="right" sortDirection={enrichmentTableSortCol === 'up_mbps' ? enrichmentTableSortDir : false} sx={{ pt: 0, pb: 0.5 }}>
          <Tooltip title="Device → CAP throughput from the newest completed iperf all-to-CAP test (uplink direction)." placement="top">
            <TableSortLabel
              active={enrichmentTableSortCol === 'up_mbps'}
              direction={enrichmentTableSortCol === 'up_mbps' ? enrichmentTableSortDir : 'asc'}
              onClick={() => toggleEnrichmentTableSort('up_mbps')}
            >
              <Typography variant="caption" color="text.secondary">Up (Mbps)</Typography>
            </TableSortLabel>
          </Tooltip>
        </TableCell>
        <TableCell align="right" sortDirection={enrichmentTableSortCol === 'down_mbps' ? enrichmentTableSortDir : false} sx={{ pt: 0, pb: 0.5 }}>
          <Tooltip title="CAP → device throughput from the newest completed iperf all-to-CAP test (reverse / downlink direction)." placement="top">
            <TableSortLabel
              active={enrichmentTableSortCol === 'down_mbps'}
              direction={enrichmentTableSortCol === 'down_mbps' ? enrichmentTableSortDir : 'asc'}
              onClick={() => toggleEnrichmentTableSort('down_mbps')}
            >
              <Typography variant="caption" color="text.secondary">Down (Mbps)</Typography>
            </TableSortLabel>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ pt: 0, pb: 0.5 }} />
      </TableRow>
    </TableHead>
    );
  }, [filteredEnrichmentCandidates, selectedEnrichmentSerials, toggleAllEnrichment, enrichmentTableSortCol, enrichmentTableSortDir, toggleEnrichmentTableSort]);

  const getSelectedSerials = useCallback(() => {
    return actionDialogSource === 'planned'
      ? Array.from(selectedPlannedSerials)
      : Array.from(selectedEnrichmentSerials);
  }, [actionDialogSource, selectedPlannedSerials, selectedEnrichmentSerials]);

  const handleCreateGroupAction = useCallback(async () => {
    const serials = getSelectedSerials();
    if (!actionGroupName.trim() || serials.length === 0) return;
    setActionSaving(true);
    try {
      const group = await api.createGroup({ name: actionGroupName.trim(), devices: serials });
      if (actionSsidId && group?.id) {
        const ssidProfile = ssidProfiles.find((p) => p.id === actionSsidId);
        const deviceTreeIds = buildCapDeviceTreeIdsForGroup(devices, serials, ssidProfile?.band);
        const assignRes = await api.assignSSIDToGroup(actionSsidId, group.id, { deviceTreeIds });
        showApiWarnings(enqueueSnackbar, assignRes);
        enqueueSnackbar(`Group "${actionGroupName}" created and SSID assigned`, { variant: 'success' });
      } else {
        enqueueSnackbar(`Group "${actionGroupName}" created with ${serials.length} device(s)`, { variant: 'success' });
      }
      if (actionDialogSource === 'planned') setSelectedPlannedSerials(new Set());
      else setSelectedEnrichmentSerials(new Set());
      setActionDialogOpen(false);
    } catch (err) {
      enqueueSnackbar(`Failed to create group: ${err?.message || err}`, { variant: 'error' });
    } finally {
      setActionSaving(false);
    }
  }, [getSelectedSerials, actionGroupName, actionSsidId, actionDialogSource, devices, ssidProfiles, enqueueSnackbar]);

  // Summarise the per-serial apply_results map the BE returns so we can show
  // a clear success / partial-success / failure snackbar in one shot.
  //
  // The BE vocabulary (see applySSIDToGroupDevices /
  // applyRadioProfileToDevices) is:
  //   - "success"
  //   - "failed: <reason>"  (note the colon variant produced by some paths)
  //   - "failed - <reason>" (other paths)
  //   - "skipped - <reason>"
  //   - "link removed (no config change)"  (unassign link-only paths)
  // We treat "failed*" as failed, "success" as succeeded, everything else as
  // skipped/informational.
  const summarizeApplyResults = useCallback((label, applyResults, totalRequested) => {
    const results = applyResults || {};
    let succeeded = 0;
    let failed = 0;
    let skipped = 0;
    Object.values(results).forEach((status) => {
      if (typeof status !== 'string') return;
      if (status === 'success') succeeded += 1;
      else if (status.startsWith('failed')) failed += 1;
      else skipped += 1;
    });
    const accounted = succeeded + failed + skipped;
    if (totalRequested > 0 && accounted === 0) {
      enqueueSnackbar(`${label}: 0 of ${totalRequested} device(s) reached the apply phase (unsupported device type or missing configuration)`, { variant: 'info' });
      return;
    }
    if (failed === 0 && skipped === 0) {
      enqueueSnackbar(`${label} applied to ${succeeded} device(s)`, { variant: 'success' });
    } else if (failed === 0) {
      enqueueSnackbar(`${label} applied to ${succeeded} device(s); ${skipped} skipped`, { variant: 'info' });
    } else {
      enqueueSnackbar(`${label}: ${succeeded} ok, ${failed} failed, ${skipped} skipped`, { variant: 'warning' });
    }
  }, [enqueueSnackbar]);

  // bulkRequestAbortRef holds the AbortController for the currently in-flight
  // bulk request so the Cancel button can really cancel it (closing the
  // dialog isn't enough: fetch would keep running and a late snackbar can
  // appear after the user has moved on).
  const bulkRequestAbortRef = useRef(null);

  const closeActionDialog = useCallback(() => {
    if (bulkRequestAbortRef.current) {
      bulkRequestAbortRef.current.abort();
      bulkRequestAbortRef.current = null;
    }
    setActionDialogOpen(false);
  }, []);

  const handleAssignSSIDAction = useCallback(async () => {
    const serials = getSelectedSerials();
    if (!actionSsidId || serials.length === 0) return;
    setActionSaving(true);
    const controller = new AbortController();
    bulkRequestAbortRef.current = controller;
    try {
      const res = await api.assignSSIDToDevices(actionSsidId, serials, { signal: controller.signal });
      if (controller.signal.aborted) return;
      showApiWarnings(enqueueSnackbar, res);
      summarizeApplyResults('SSID', res?.apply_results, serials.length);
      if (actionDialogSource === 'planned') setSelectedPlannedSerials(new Set());
      else setSelectedEnrichmentSerials(new Set());
      setActionDialogOpen(false);
    } catch (err) {
      if (err?.name === 'AbortError' || controller.signal.aborted) return;
      enqueueSnackbar(`Failed to assign SSID: ${err?.message || err}`, { variant: 'error' });
    } finally {
      if (bulkRequestAbortRef.current === controller) bulkRequestAbortRef.current = null;
      setActionSaving(false);
    }
  }, [getSelectedSerials, actionSsidId, actionDialogSource, enqueueSnackbar, summarizeApplyResults]);

  const handleAssignRadioProfileAction = useCallback(async () => {
    const serials = getSelectedSerials();
    if (!actionRadioProfileId || serials.length === 0) return;
    setActionSaving(true);
    const controller = new AbortController();
    bulkRequestAbortRef.current = controller;
    try {
      const res = await api.assignRadioProfileToDevices(actionRadioProfileId, serials, { signal: controller.signal });
      if (controller.signal.aborted) return;
      showApiWarnings(enqueueSnackbar, res);
      summarizeApplyResults('Radio profile', res?.apply_results, serials.length);
      if (actionDialogSource === 'planned') setSelectedPlannedSerials(new Set());
      else setSelectedEnrichmentSerials(new Set());
      setActionDialogOpen(false);
    } catch (err) {
      if (err?.name === 'AbortError' || controller.signal.aborted) return;
      enqueueSnackbar(`Failed to assign radio profile: ${err?.message || err}`, { variant: 'error' });
    } finally {
      if (bulkRequestAbortRef.current === controller) bulkRequestAbortRef.current = null;
      setActionSaving(false);
    }
  }, [getSelectedSerials, actionRadioProfileId, actionDialogSource, enqueueSnackbar, summarizeApplyResults]);

  const handleAddToGroupAction = useCallback(async () => {
    const serials = getSelectedSerials();
    if (!actionGroupId || serials.length === 0) return;
    setActionSaving(true);
    try {
      await api.updateGroup(actionGroupId, { add_devices: serials });
      const grp = groups.find(g => g.id === actionGroupId);
      enqueueSnackbar(`Added ${serials.length} device(s) to group "${grp?.name || actionGroupId}"`, { variant: 'success' });
      if (actionDialogSource === 'planned') setSelectedPlannedSerials(new Set());
      else setSelectedEnrichmentSerials(new Set());
      setActionDialogOpen(false);
    } catch (err) {
      enqueueSnackbar(`Failed to add to group: ${err?.message || err}`, { variant: 'error' });
    } finally {
      setActionSaving(false);
    }
  }, [getSelectedSerials, actionGroupId, actionDialogSource, groups, enqueueSnackbar]);

  // Map bounds from API data
  const mapBounds = useMemo(() => {
    const points = [
      ...mapSelectedPlannedEntries.filter((e) => e.lat && e.lng).map((e) => ({ lat: e.lat, lng: e.lng })),
      ...mapSelectedEnrichmentCandidates.map((c) => ({ lat: c.lat, lng: c.lng })),
    ].filter(p => p.lat && p.lng);
    if (!points.length) return null;
    const lats = points.map(p => p.lat);
    const lngs = points.map(p => p.lng);
    return {
      north: Math.max(...lats),
      south: Math.min(...lats),
      east: Math.max(...lngs),
      west: Math.min(...lngs),
    };
  }, [mapSelectedPlannedEntries, mapSelectedEnrichmentCandidates]);

  const hasMapSelection = mapBounds != null;

  const mapCenter = useMemo(() => {
    if (!mapBounds) return mapViewportRef.current.center;
    return {
      lat: (mapBounds.north + mapBounds.south) / 2,
      lng: (mapBounds.east + mapBounds.west) / 2,
    };
  }, [mapBounds]);

  const mapZoom = useMemo(() => {
    if (!mapBounds) return mapViewportRef.current.zoom;
    const latSpread = mapBounds.north - mapBounds.south;
    const lngSpread = mapBounds.east - mapBounds.west;
    const maxSpread = Math.max(latSpread, lngSpread);
    if (maxSpread > 1) return 8;
    if (maxSpread > 0.5) return 10;
    if (maxSpread > 0.1) return 12;
    if (maxSpread > 0.01) return 14;
    return 16;
  }, [mapBounds]);

  const syncMapViewportFromMap = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const center = map.getCenter();
    const zoom = map.getZoom();
    if (!center || zoom == null) return;
    mapViewportRef.current = {
      center: { lat: center.lat(), lng: center.lng() },
      zoom,
    };
  }, []);

  const fitMapToSelection = useCallback(() => {
    const map = mapRef.current;
    if (!map || !window.google?.maps) return;
    try {
      const bounds = new window.google.maps.LatLngBounds();
      let hasBounds = false;
      const addPoint = (lat, lng) => {
        bounds.extend(new window.google.maps.LatLng(lat, lng));
        hasBounds = true;
      };
      mapSelectedPlannedEntries.forEach((e) => { if (e.lat && e.lng) addPoint(e.lat, e.lng); });
      mapSelectedEnrichmentCandidates.forEach((c) => { if (c.lat && c.lng) addPoint(c.lat, c.lng); });
      if (!hasBounds) return;
      map.fitBounds(bounds);
      window.google.maps.event.addListenerOnce(map, 'idle', syncMapViewportFromMap);
    } catch (e) {
      console.error('Error fitting map bounds:', e);
    }
  }, [mapSelectedPlannedEntries, mapSelectedEnrichmentCandidates, syncMapViewportFromMap]);

  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;
    if (hasMapSelection) {
      fitMapToSelection();
    } else {
      map.setCenter(mapViewportRef.current.center);
      map.setZoom(mapViewportRef.current.zoom);
    }
  }, [hasMapSelection, fitMapToSelection]);

  const handleMapIdle = useCallback(() => {
    syncMapViewportFromMap();
  }, [syncMapViewportFromMap]);

  // Keep the current pan/zoom when the last map checkbox is cleared.
  useEffect(() => {
    if (hadMapSelectionRef.current && !hasMapSelection) {
      syncMapViewportFromMap();
    }
    hadMapSelectionRef.current = hasMapSelection;
  }, [hasMapSelection, syncMapViewportFromMap]);

  // Refit when selected devices load after map mount (e.g. returning from device details).
  useEffect(() => {
    if (!isActive || !isLoaded || !hasMapSelection) return;
    fitMapToSelection();
    const map = mapRef.current;
    if (map && window.google?.maps?.event) {
      window.google.maps.event.trigger(map, 'resize');
      fitMapToSelection();
    }
  }, [isActive, isLoaded, hasMapSelection, fitMapToSelection, mapSelectedPlannedEntries, mapSelectedEnrichmentCandidates]);

  const getRrmMapMarkerStyle = useCallback((serial) => {
    return computeRrmMapMarkerStyle(serial, mapPlanEntryMapForStyle, publicWifiAnySerials);
  }, [mapPlanEntryMapForStyle, publicWifiAnySerials]);

  const mapLegendRows = useMemo(() => {
    const mapSerials = [
      ...mapSelectedPlannedEntries.map((e) => e.serial),
      ...mapSelectedEnrichmentCandidates.map((c) => c.serial),
    ];
    let showPlanP = false;
    let showPlanW = false;
    let showNoPlanN = false;
    let showNoPlanW = false;
    for (const serial of mapSerials) {
      const s = (serial || '').toLowerCase();
      const inPlan = !!mapPlanEntryMapForStyle[s];
      const hasPW = publicWifiAnySerials.has(s);
      if (inPlan) {
        if (hasPW) showPlanW = true;
        else showPlanP = true;
      } else if (hasPW) {
        showNoPlanW = true;
      } else {
        showNoPlanN = true;
      }
    }
    const rows = [];
    if (showPlanP) {
      rows.push({ id: 'plan-p', color: MAP_UI.rrmPlanMarker, label: 'P', text: 'In deployment plan · Public WiFi not enabled yet' });
    }
    if (showPlanW) {
      rows.push({ id: 'plan-w', color: MAP_UI.rrmPlanMarker, label: 'W', text: 'In plan · Public WiFi already enabled' });
    }
    if (showNoPlanN) {
      rows.push({ id: 'noplan-n', color: MAP_UI.deviceOnline, label: 'N', text: 'Not in plan · no Public WiFi' });
    }
    if (showNoPlanW) {
      rows.push({ id: 'noplan-w', color: MAP_UI.deviceOffline, label: 'W', text: 'Not in plan · Public WiFi enabled' });
    }
    return rows;
  }, [
    mapSelectedPlannedEntries,
    mapSelectedEnrichmentCandidates,
    mapPlanEntryMapForStyle,
    publicWifiAnySerials,
  ]);

  const getMarkerColor = (score) => {
    if (score >= 85) return MAP_UI.deviceOnlineBorder;
    if (score >= 70) return MAP_UI.deviceOnline;
    if (score >= 55) return MAP_UI.accentMuted;
    return '#9e9e9e';
  };

  const getScoreChipSx = (score) => ({
    bgcolor: getMarkerColor(score),
    color: score >= 55 ? '#fff' : MAP_UI.publicWifiStat,
    fontWeight: 600,
    fontSize: '0.875rem',
  });

  const createMarkerIcon = (color, isPublicWifi = false, isSelectedAP = false, label = null) => {
    const defaultLabel = isSelectedAP ? 'AP' : (isPublicWifi ? 'W' : 'P');
    const icon = label !== null ? label : defaultLabel;
    const size = isSelectedAP ? 32 : 26;
    const iconStr = String(icon);
    const fontSize = isSelectedAP
      ? '12px'
      : (iconStr.length > 1 ? '8px' : '10px');
    const div = document.createElement('div');
    div.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: center;
      width: ${size}px;
      height: ${size}px;
      background-color: ${color};
      border: ${isSelectedAP ? '3px' : '2px'} solid #fff;
      border-radius: 50%;
      color: #fff;
      font-size: ${fontSize};
      font-weight: bold;
      font-family: Arial, sans-serif;
      cursor: pointer;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    `;
    div.textContent = icon;
    return div;
  };

  const goToDevice = useCallback((serial) => {
    if (!serial) return;
    navigate(`/device/${serial}`);
  }, [navigate]);

  const stats = useMemo(() => {
    const cands = rrmData?.candidates || [];
    return {
      // Exclude critical parents — they can never get public WiFi and skew the count.
      totalEligible: (rrmData?.summary?.total_eligible ?? 0) - (rrmData?.summary?.critical_parents ?? 0),
      excellentCandidates: cands.filter(c => c.recommendation === 'excellent').length,
      goodCandidates: cands.filter(c => c.recommendation === 'good').length,
      possibleCandidates: cands.filter(c => c.recommendation === 'possible').length,
      existingPublicWifi: allDevicesWithPublicWifi.length,
    };
  }, [rrmData, allDevicesWithPublicWifi]);

  const statusBanner = useMemo(() => {
    if (rrmLoading) {
      return { severity: 'info', icon: '⏳', message: 'Computing RRM deployment plan...' };
    }
    if (rrmError) {
      return { severity: 'error', icon: '❌', message: rrmError };
    }
    if (deploymentPlanMode && rrmData?.summary) {
      const s = rrmData.summary;
      const iperf = rrmData.iperf_backhaul;
      let iperfNote = '';
      if (iperf?.enabled) {
        const dirLabel = iperf.direction === 'uplink' ? 'uplink'
          : iperf.direction === 'downlink' ? 'downlink' : 'avg up/down';
        const reqLabel = iperf.require_iperf ? 'iperf required for APs' : 'iperf preferred';
        iperfNote = ` · iperf ${dirLabel} (${iperf.devices_with_iperf}/${(iperf.devices_with_iperf || 0) + (iperf.devices_without_iperf || 0)} measured, ${reqLabel})`;
      }
      return {
        severity: 'success',
        icon: '✅',
        message: `Deployment Plan: ${s.plan_5ghz_count} AP NODE(s) selected, ${s.coverage_pct.toFixed(1)}% coverage${iperfNote}`,
      };
    }
    if (!rrmData) {
      return {
        severity: 'info',
        icon: 'ℹ️',
        message: 'Click "Generate Deployment Plan" to compute the RRM analysis',
      };
    }
    const planDate = rrmData.computed_at
      ? new Date(rrmData.computed_at).toLocaleString()
      : null;
    const recommended = (rrmData.candidates || []).filter(c => c.recommendation !== 'not_recommended').length;
    if (recommended > 0) {
      return {
        severity: 'info',
        icon: 'ℹ️',
        message: `Showing last saved plan${planDate ? ` from ${planDate}` : ''} — ${recommended} NODE${recommended !== 1 ? 's' : ''} recommended`,
      };
    }
    return {
      severity: 'info',
      icon: 'ℹ️',
      message: `Showing last saved plan${planDate ? ` from ${planDate}` : ''} — no recommended nodes`,
    };
  }, [rrmLoading, rrmError, deploymentPlanMode, rrmData]);

  const getRecommendationLabel = (recommendation) => {
    switch (recommendation) {
      case 'excellent': return 'Excellent candidate';
      case 'good': return 'Good candidate';
      case 'possible': return 'Possible candidate';
      case 'excluded': return 'Excluded (critical parent)';
      default: return 'Not recommended';
    }
  };

  const getRecommendationChipSx = (recommendation) => {
    switch (recommendation) {
      case 'excellent':
      case 'good':
        return healthChipSoftSx('success');
      case 'possible':
        return healthChipSoftSx('warning');
      case 'excluded':
        return healthChipSoftSx('error');
      default:
        return { height: 20 };
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
          WiFi RRM
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Suggested locations and RF tuning for Wi‑Fi based on backhaul quality and coverage gaps
        </Typography>
      </Box>

      {/* Status Banner - Always Visible */}
      <Alert
        severity={statusBanner.severity}
        icon={false}
        sx={{
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          '& .MuiAlert-message': {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          <span style={{ fontSize: '1.2em', marginRight: '8px' }}>{statusBanner.icon}</span>
          {statusBanner.message}
        </Typography>
      </Alert>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 2, ...publicWifiStatSx.card }}>
            <Typography variant="caption" color="text.secondary">
              Eligible NODEs
            </Typography>
            <Typography variant="h4" sx={publicWifiStatSx.value}>
              {stats.totalEligible}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 2, ...publicWifiStatSx.card }}>
            <Typography variant="caption" color="text.secondary">
              Excellent Candidates
            </Typography>
            <Typography variant="h4" sx={publicWifiStatSx.value}>
              {stats.excellentCandidates}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 2, ...publicWifiStatSx.card }}>
            <Typography variant="caption" color="text.secondary">
              Good Candidates
            </Typography>
            <Typography variant="h4" sx={publicWifiStatSx.value}>
              {stats.goodCandidates}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 2, ...publicWifiStatSx.card }}>
            <Tooltip
              title="jnet2 NODE devices that currently have an active Public WiFi SSID configured (AP mode interface). CAP devices are excluded. Offline devices with incomplete interface data may not be counted."
              placement="top"
            >
              <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                NODEs with Public WiFi
              </Typography>
            </Tooltip>
            <Typography variant="h4" sx={publicWifiStatSx.value}>
              {stats.existingPublicWifi}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Configuration Panel */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
          Configuration
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Coverage Radius: {coverageRadius}m
            </Typography>
            <Slider
              value={coverageRadius}
              onChange={(_e, newValue) => setCoverageRadius(newValue)}
              min={10}
              max={100}
              step={5}
              marks={[
                { value: 10, label: '10m' },
                { value: 30, label: '30m' },
                { value: 50, label: '50m' },
                { value: 100, label: '100m' }
              ]}
              valueLabelDisplay="auto"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Coverage radius used for the deployment plan optimization. Changing this in plan mode recomputes automatically.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={hideLowScore}
                  onChange={(e) => setHideLowScore(e.target.checked)}
                  color="primary"
                />
              }
              label="Show only recommended candidates"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              When enabled, hides not-recommended candidates from the table to focus on actionable devices.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                onClick={() => setIperfOptionsExpanded((v) => !v)}
                sx={{ cursor: 'pointer', p: 2, pb: iperfOptionsExpanded ? 1 : 2, userSelect: 'none' }}
              >
                <IconButton size="small" tabIndex={-1}>
                  {iperfOptionsExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Iperf backhaul
                    </Typography>
                    {useIperfBackhaul && (
                      <Chip label={`Plan: ${iperfDirectionLabel}`} size="small" variant="outlined" sx={rrmPlanChipSx} />
                    )}
                    {useIperfBackhaul && requireIperf && (
                      <Chip label="Require iperf" size="small" variant="outlined" sx={rrmPlanChipSx} />
                    )}
                    {backhaulFilterActive && (
                      <Chip label="Table filter" size="small" variant="outlined" sx={rrmPlanChipSx} />
                    )}
                    {!useIperfBackhaul && !backhaulFilterActive && (
                      <Chip label="Latency only" size="small" variant="outlined" sx={rrmPlanChipSx} />
                    )}
                  </Stack>
                  {!iperfOptionsExpanded && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      {useIperfBackhaul
                        ? `Deployment plan uses iperf (${iperfDirectionLabel})${requireIperf ? ', APs require measurements' : ''}`
                        : 'Deployment plan uses latency backhaul'}
                      {backhaulFilterActive && rrmData
                        ? ` · tables filtered to ${filteredPlannedApRows.length}/${plannedApRows.length} planned AP(s)`
                        : ''}
                    </Typography>
                  )}
                </Box>
              </Stack>
              <Collapse in={iperfOptionsExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ px: 2, pb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Uses newest completed all-to-CAP iperf tests (device→CAP uplink, CAP→device downlink).
                  </Typography>

                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
                    Deployment plan
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: 'action.hover' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={useIperfBackhaul}
                          onChange={(e) => setUseIperfBackhaul(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Prioritize plan by iperf backhaul"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4.5, mb: 1.5 }}>
                      Replaces latency scoring with iperf throughput when measurements exist.
                    </Typography>
                    <FormControl
                      fullWidth
                      size="small"
                      disabled={!useIperfBackhaul}
                      sx={{ maxWidth: 360, ml: 4.5 }}
                    >
                      <InputLabel>Direction</InputLabel>
                      <Select
                        value={iperfDirection}
                        label="Direction"
                        onChange={(e) => setIperfDirection(e.target.value)}
                      >
                        <MenuItem value="both">Both (average)</MenuItem>
                        <MenuItem value="uplink">Uplink (device → CAP)</MenuItem>
                        <MenuItem value="downlink">Downlink (CAP → device)</MenuItem>
                      </Select>
                    </FormControl>
                  </Paper>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={requireIperf}
                        onChange={(e) => setRequireIperf(e.target.checked)}
                        color="primary"
                        disabled={!useIperfBackhaul}
                      />
                    }
                    label="Require iperf for plan APs"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4.5 }}>
                    Only measured devices can host 5 GHz public WiFi in the plan.
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
                    Table filter
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Hide planned AP and enrichment rows below minimum iperf Mbps. Map visibility follows checkbox selections.
                  </Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="flex-start">
                    <TextField
                      label="Min up (Mbps)"
                      type="number"
                      size="small"
                      value={minBackhaulUpMbps}
                      onChange={(e) => setMinBackhaulUpMbps(e.target.value)}
                      inputProps={{ min: 0, step: 1 }}
                      helperText="Device → CAP"
                      sx={{ width: 160 }}
                    />
                    <TextField
                      label="Min down (Mbps)"
                      type="number"
                      size="small"
                      value={minBackhaulDownMbps}
                      onChange={(e) => setMinBackhaulDownMbps(e.target.value)}
                      inputProps={{ min: 0, step: 1 }}
                      helperText="CAP → device"
                      sx={{ width: 160 }}
                    />
                    {backhaulFilterActive && (
                      <Button
                        size="small"
                        onClick={() => { setMinBackhaulUpMbps(''); setMinBackhaulDownMbps(''); }}
                        sx={{ mt: 0.5 }}
                      >
                        Clear filter
                      </Button>
                    )}
                  </Stack>
                  {backhaulFilterActive && rrmData && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Showing {filteredPlannedApRows.length} of {plannedApRows.length} planned AP(s)
                      {enrichmentCandidates.length > 0 && (
                        <> · {filteredEnrichmentCandidates.length} of {enrichmentCandidates.length} enrichment candidate(s)</>
                      )}
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Coverage-Optimized Deployment Planning */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Public WiFi Deployment Planning
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Generate a coverage-optimized 5 GHz Public WiFi deployment plan. Use Configuration to assign SSID/radio to devices, guided by the last saved plan.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Generate / Recompute Plan */}
            <Button
              variant={deploymentPlanMode ? 'outlined' : 'contained'}
              color="primary"
              size="large"
              startIcon={
                rrmLoading
                  ? <CircularProgress size={18} color="inherit" />
                  : deploymentPlanMode ? <CheckCircleIcon /> : <PlayArrowIcon />
              }
              onClick={() => generatePlan(coverageRadius, iperfPlanOptions)}
              disabled={rrmLoading || !canMutate}
            >
              {rrmLoading ? 'Computing...' : deploymentPlanMode ? 'Recompute Plan' : 'Generate Deployment Plan'}
            </Button>
          </Box>
        </Box>

        {/* Info when no plan generated yet */}
        {!deploymentPlanMode && !rrmData && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Generate a Public WiFi Deployment Plan
            </Typography>
            <Typography variant="body2">
              Click "Generate Deployment Plan" to create an optimal WiFi layout based on NODE locations,
              coverage requirements, and backhaul quality. Critical tree management parents are automatically
              excluded to avoid 5GHz backhaul conflicts.
            </Typography>
          </Alert>
        )}

        {/* Warning when plan has no APs */}
        {deploymentPlanMode && rrmData?.plan_5ghz?.length === 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              No Deployment Plan Generated
            </Typography>
            <Typography variant="body2" component="div">
              Unable to generate a plan — all eligible NODEs may be critical tree management parents.
              {rrmData?.excluded?.length > 0 && (
                <Box component="ul" sx={{ m: 0, mt: 1, pl: 2 }}>
                  <li>{rrmData.excluded.length} NODE(s) excluded as critical tree management parents</li>
                </Box>
              )}
            </Typography>
          </Alert>
        )}

        {deploymentPlanMode && rrmData?.plan_5ghz?.length > 0 && (
          <Box>
            {/* Plan Statistics */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={1} sx={publicWifiStatSx.planStatPaper}>
                  <Typography variant="caption" sx={publicWifiStatSx.planStatCaption}>Eligible NODEs</Typography>
                  <Typography variant="h5" sx={publicWifiStatSx.planStatValue}>
                    {(rrmData?.summary?.total_eligible ?? 0) - (rrmData?.summary?.critical_parents ?? 0)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={1} sx={publicWifiStatSx.planStatPaper}>
                  <Typography variant="caption" sx={publicWifiStatSx.planStatCaption}>Covered NODEs</Typography>
                  <Typography variant="h5" sx={publicWifiStatSx.planStatValue}>
                    {coveredSerialSet.size}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={1} sx={publicWifiStatSx.planStatPaper}>
                  <Typography variant="caption" sx={publicWifiStatSx.planStatCaption}>APs Selected</Typography>
                  <Typography variant="h5" sx={publicWifiStatSx.planStatValue}>
                    {rrmData?.plan_5ghz?.length ?? 0}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={1} sx={publicWifiStatSx.planStatPaper}>
                  <Typography variant="caption" sx={publicWifiStatSx.planStatCaption}>Coverage</Typography>
                  <Typography variant="h5" sx={publicWifiStatSx.planStatValue}>
                    {(rrmData?.summary?.coverage_pct ?? 0).toFixed(1)}%
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Conflict alert — critical parents that currently have 5 GHz Public WiFi */}
            {conflictingExcluded.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Operational Conflict: {conflictingExcluded.length} critical parent NODE(s) have 5 GHz Public WiFi active
                </Typography>
                <Typography variant="body2">
                  The following NODEs are classified as critical tree management parents and must NOT run
                  5 GHz Public WiFi (risk of backhaul interference), but currently do:{' '}
                  <strong>{conflictingExcluded.map(e => e.serial).join(', ')}</strong>.
                  Remove the 5 GHz SSID assignment from these devices immediately.
                </Typography>
              </Alert>
            )}

            {/* Excluded devices info */}
            {rrmData.excluded?.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{rrmData.excluded.length}</strong> NODE(s) excluded from AP selection — critical tree management
                  parents whose children have no alternative backhaul paths.
                </Typography>
              </Alert>
            )}

          </Box>
        )}
      </Paper>

      {/* Map Section — always before tables (normal and deployment plan mode) */}
      {(rrmData?.candidates?.length > 0 || rrmData?.excluded?.length > 0 || (rrmData?.plan_5ghz?.length > 0) || allDevicesWithPublicWifi.length > 0) && (
        <Paper elevation={2} sx={{ mb: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {deploymentPlanMode ? 'Deployment Plan Visualization' : 'Recommended Locations'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {mapSelectedPlannedEntries.length} planned AP(s) and {mapSelectedEnrichmentCandidates.length} enrichment candidate(s) selected on map
                {backhaulFilterActive ? ' · iperf table filter active' : ''}
              </Typography>
            </Box>
            <Button
              variant={showConnections ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setShowConnections(v => !v)}
              sx={{
                backgroundColor: showConnections ? MAP_UI.accent : undefined,
                borderColor: MAP_UI.accent,
                color: showConnections ? 'white' : MAP_UI.accent,
                '&:hover': { backgroundColor: showConnections ? MAP_UI.accentDark : MAP_UI.accentTintHover },
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                flexShrink: 0,
                ml: 2,
              }}
            >
              {showConnections ? 'Hide Backhaul Connections' : 'Show Backhaul Connections'}
            </Button>
          </Box>
          <Box sx={{ position: 'relative', width: '100%', height: { xs: '400px', md: '650px' } }}>
            {loadError ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Alert severity="info" sx={{ m: 2 }}>
                  Map unavailable. Recommendations remain functional.
                </Alert>
              </Box>
            ) : !isLoaded ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
              <GoogleMap
                key={`public-wifi-expansion-map-${theme.palette.mode}`}
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={hasMapSelection ? mapCenter : mapViewportRef.current.center}
                zoom={hasMapSelection ? mapZoom : mapViewportRef.current.zoom}
                onLoad={handleMapLoad}
                onIdle={handleMapIdle}
                options={{
                  streetViewControl: false,
                  mapTypeControl: true,
                  fullscreenControl: true,
                  mapId: 'PUBLIC_WIFI_EXPANSION_MAP',
                  colorScheme: mapColorScheme,
                }}
              >
                {/* DEPLOYMENT PLAN MODE: checked planned APs + checked enrichment candidates */}
                {deploymentPlanMode && rrmData && (
                  <>
                    {mapSelectedPlannedEntries.map((planEntry) => {
                      const serialLower = (planEntry.serial || '').toLowerCase();
                      const apStyle = getRrmMapMarkerStyle(planEntry.serial);
                      const apHasPW = publicWifiAnySerials.has(serialLower);
                      const apHas5G = publicWifi5GHzSerials.has(serialLower);
                      return (
                        <React.Fragment key={`ap-${planEntry.serial}`}>
                          <AdvancedMarker
                            position={{ lat: planEntry.lat, lng: planEntry.lng }}
                            content={createMarkerIcon(apStyle.color, false, true, apStyle.label)}
                            onClick={() => setSelectedDevice({
                              device: planEntry,
                              type: 'selectedAP',
                              lat: planEntry.lat,
                              lng: planEntry.lng,
                            })}
                            title={`AP: ${planEntry.serial} — Covers ${planEntry.covered_node_count} NODEs${apHas5G ? ' · Already has 5GHz Public WiFi' : apHasPW ? ' · Already has Public WiFi' : ''}`}
                          />
                          <Circle
                            center={{ lat: planEntry.lat, lng: planEntry.lng }}
                            radius={coverageRadius}
                            options={{
                              fillColor: MAP_UI.deviceOnline,
                              fillOpacity: 0.2,
                              strokeColor: MAP_UI.deviceOnlineBorder,
                              strokeOpacity: 0.45,
                              strokeWeight: 2,
                            }}
                          />
                        </React.Fragment>
                      );
                    })}

                    {mapSelectedEnrichmentCandidates.map((cand) => {
                      const serialLower = (cand.serial || '').toLowerCase();
                      const apStyle = getRrmMapMarkerStyle(cand.serial);
                      const apHasPW = publicWifiAnySerials.has(serialLower);
                      const apHas5G = publicWifi5GHzSerials.has(serialLower);
                      return (
                        <React.Fragment key={`ap-enrich-${cand.serial}`}>
                          <AdvancedMarker
                            position={{ lat: cand.lat, lng: cand.lng }}
                            content={createMarkerIcon(apStyle.color, false, true, apStyle.label)}
                            onClick={() => setSelectedDevice({
                              device: cand,
                              type: 'candidate',
                              lat: cand.lat,
                              lng: cand.lng,
                            })}
                            title={`AP: ${cand.serial} — Score: ${cand.score_5ghz?.toFixed?.(0) ?? '—'}${apHas5G ? ' · Already has 5GHz Public WiFi' : apHasPW ? ' · Already has Public WiFi' : ''}`}
                          />
                          <Circle
                            center={{ lat: cand.lat, lng: cand.lng }}
                            radius={coverageRadius}
                            options={{
                              fillColor: MAP_UI.deviceOnline,
                              fillOpacity: 0.2,
                              strokeColor: MAP_UI.deviceOnlineBorder,
                              strokeOpacity: 0.45,
                              strokeWeight: 2,
                            }}
                          />
                        </React.Fragment>
                      );
                    })}
                  </>
                )}

                {/* NORMAL MODE: checked planned AP circles + checked enrichment markers */}
                {!deploymentPlanMode && (
                  <>
                    {mapSelectedPlannedEntries.map((planEntry) => (
                      planEntry.lat && planEntry.lng ? (
                        <Circle
                          key={`plan-circle-${planEntry.serial}`}
                          center={{ lat: planEntry.lat, lng: planEntry.lng }}
                          radius={coverageRadius}
                          options={{
                            fillColor: MAP_UI.deviceOnline,
                            fillOpacity: 0.15,
                            strokeColor: MAP_UI.deviceOnlineBorder,
                            strokeOpacity: 0.4,
                            strokeWeight: 2,
                          }}
                        />
                      ) : null
                    ))}

                    {mapSelectedEnrichmentCandidates.map((cand) => {
                      const serialLower = (cand.serial || '').toLowerCase();
                      const apStyle = getRrmMapMarkerStyle(cand.serial);
                      const apHasPW = publicWifiAnySerials.has(serialLower);
                      const apHas5G = publicWifi5GHzSerials.has(serialLower);
                      return (
                        <React.Fragment key={`cand-${cand.serial}`}>
                          <Circle
                            center={{ lat: cand.lat, lng: cand.lng }}
                            radius={coverageRadius}
                            options={{
                              fillColor: MAP_UI.deviceOnline,
                              fillOpacity: 0.15,
                              strokeColor: MAP_UI.deviceOnlineBorder,
                              strokeOpacity: 0.4,
                              strokeWeight: 2,
                            }}
                          />
                          <AdvancedMarker
                            position={{ lat: cand.lat, lng: cand.lng }}
                            content={createMarkerIcon(apStyle.color, false, true, apStyle.label)}
                            onClick={() => setSelectedDevice({
                              device: cand,
                              type: 'candidate',
                              lat: cand.lat,
                              lng: cand.lng,
                            })}
                            title={`AP: ${cand.serial} — Score: ${cand.score_5ghz?.toFixed?.(0) ?? '—'}${apHas5G ? ' · Already has 5GHz Public WiFi' : apHasPW ? ' · Already has Public WiFi' : ''}`}
                          />
                        </React.Fragment>
                      );
                    })}
                  </>
                )}

                {/* Backhaul connection lines */}
                {showConnections && connectionLines.map(conn => (
                  <React.Fragment key={conn.key}>
                    <Polyline
                      path={[
                        { lat: conn.from.lat, lng: conn.from.lng },
                        { lat: conn.to.lat, lng: conn.to.lng },
                      ]}
                      options={{
                        strokeColor: conn.color,
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        icons: [{
                          icon: {
                            path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW,
                            scale: 3,
                            strokeColor: conn.color,
                            fillColor: conn.color,
                            fillOpacity: 1,
                          },
                          offset: '50%',
                        }],
                      }}
                    />
                    <OverlayView
                      position={{ lat: conn.midLat, lng: conn.midLng }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <Tooltip
                        title={conn.rssi != null ? `RSSI: ${conn.rssi} dBm · Distance: ${conn.distText}` : `Distance: ${conn.distText}`}
                        placement="top"
                        arrow
                      >
                        <Box sx={{
                          px: 0.6,
                          py: 0.2,
                          bgcolor: 'rgba(0,0,0,0.65)',
                          color: '#fff',
                          borderRadius: 1,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          cursor: 'default',
                          userSelect: 'none',
                        }}>
                          {conn.label}
                        </Box>
                      </Tooltip>
                    </OverlayView>
                  </React.Fragment>
                ))}

                {/* Info Window */}
                {selectedDevice && (
                  <InfoWindow
                    position={{
                      lat: selectedDevice.lat || selectedDevice.device?.lat,
                      lng: selectedDevice.lng || selectedDevice.device?.lng,
                    }}
                    onCloseClick={() => setSelectedDevice(null)}
                  >
                    <Box sx={{ p: 1, minWidth: 250 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        {selectedDevice.device?.serial || selectedDevice.device?.serial_number || selectedDevice.device?.name}
                      </Typography>

                      {selectedDevice.type === 'selectedAP' ? (
                        (() => {
                          const c = candidateMap[selectedDevice.device.serial];
                          const apHas5G = publicWifi5GHzSerials.has((selectedDevice.device.serial || '').toLowerCase());
                          return (
                            <>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                                <Chip label="Selected AP" size="small" sx={healthChipSoftSx('success')} />
                                {apHas5G && <Chip label="5G ✓ Already configured" size="small" variant="outlined" sx={rrmPlanChipSx} />}
                              </Box>
                              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                <strong>NODEs Covered:</strong> {selectedDevice.device.covered_node_count}
                              </Typography>
                              {c?.latency_ms != null && (
                                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                  <strong>Latency:</strong> {formatLatencyMs(c.latency_ms)}
                                </Typography>
                              )}
                              {c?.tx_failed_pct != null && (
                                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                  <strong>TX Failed:</strong> {(c.tx_failed_pct * 100).toFixed(1)}%
                                </Typography>
                              )}
                              {c?.backhaul_score != null && (
                                <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                                  <strong>Backhaul Score:</strong> {c.backhaul_score.toFixed(1)}
                                </Typography>
                              )}
                            </>
                          );
                        })()
                      ) : selectedDevice.type === 'candidate' ? (
                        (() => {
                          const candHas5G = publicWifi5GHzSerials.has((selectedDevice.device?.serial || '').toLowerCase());
                          return (
                            <>
                              {candHas5G && <Chip label="5G ✓ Already configured" size="small" variant="outlined" sx={{ mb: 1, ...rrmPlanChipSx }} />}
                              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                <strong>Score:</strong> {selectedDevice.device.score_5ghz?.toFixed(0)}/100
                              </Typography>
                              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                <strong>Classification:</strong> {selectedDevice.device.classification}
                              </Typography>
                              {selectedDevice.device.latency_ms != null && (
                                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                  <strong>Latency:</strong> {formatLatencyMs(selectedDevice.device.latency_ms)}
                                </Typography>
                              )}
                              {selectedDevice.device.tx_failed_pct != null && (
                                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                  <strong>TX Failed:</strong> {(selectedDevice.device.tx_failed_pct * 100).toFixed(1)}%
                                </Typography>
                              )}
                              {selectedDevice.device.reasons?.length > 0 && (
                                <>
                                  <Typography variant="caption" display="block" sx={{ mb: 1, fontWeight: 600 }}>
                                    Why Recommended:
                                  </Typography>
                                  {selectedDevice.device.reasons.map((reason, idx) => (
                                    <Typography
                                      key={idx}
                                      variant="caption"
                                      display="block"
                                      sx={{ mb: 0.5, fontStyle: 'italic', color: 'text.secondary' }}
                                    >
                                      • {reason}
                                    </Typography>
                                  ))}
                                </>
                              )}
                            </>
                          );
                        })()
                      ) : selectedDevice.type === 'node' ? (
                        (() => {
                          const nodeHas5G = publicWifi5GHzSerials.has((selectedDevice.device?.serial || '').toLowerCase());
                          return (
                            <>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                                <Chip
                                  label={selectedDevice.isCovered ? 'Covered' : 'Uncovered'}
                                  size="small"
                                  sx={healthChipSoftSx(selectedDevice.isCovered ? 'success' : 'warning')}
                                />
                                {nodeHas5G && <Chip label="5G ✓ Already configured" size="small" variant="outlined" sx={rrmPlanChipSx} />}
                              </Box>
                              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                {selectedDevice.isCovered
                                  ? 'This NODE is within coverage radius of a selected AP'
                                  : 'This NODE is not covered by any selected AP'}
                              </Typography>
                            </>
                          );
                        })()
                      ) : selectedDevice.type === 'excluded' ? (
                        (() => {
                          const isConflict = publicWifi5GHzSerials.has((selectedDevice.device?.serial || '').toLowerCase());
                          return (
                            <>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                                <Chip label="Critical Parent — Excluded" size="small" variant="outlined" sx={rrmPlanChipSx} />
                                {isConflict && <Chip label="⚠ 5GHz WiFi Active — Conflict!" size="small" sx={healthChipSoftSx('error')} />}
                              </Box>
                              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                <strong>Children:</strong> {selectedDevice.device.child_count}
                              </Typography>
                              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                <strong>Criticality Score:</strong> {selectedDevice.device.criticality_score?.toFixed(2)}
                              </Typography>
                              {isConflict && (
                                <Typography variant="caption" display="block" sx={{ mb: 0.5, color: MAP_UI.offlineChipText, fontWeight: 600 }}>
                                  Remove 5 GHz SSID — backhaul conflict risk
                                </Typography>
                              )}
                              <Typography variant="caption" display="block" sx={{ mb: 0.5, color: 'text.secondary' }}>
                                {selectedDevice.device.reason}
                              </Typography>
                            </>
                          );
                        })()
                      ) : (
                        <>
                          <Chip label="Public WiFi Enabled" size="small" variant="outlined" sx={{ mb: 1, ...rrmPlanChipSx }} />
                          <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                            Existing Public WiFi access point
                          </Typography>
                        </>
                      )}

                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        {(selectedDevice.type === 'selectedAP'
                          || selectedDevice.type === 'candidate'
                          || selectedDevice.type === 'node') && (
                          <Tooltip
                            title={
                              selectedDevice.type === 'selectedAP'
                                ? 'This AP will not be part of the deployment plan'
                                : 'This device will not be included in the deployment plan'
                            }
                            placement="top"
                            arrow
                          >
                            <Box
                              component="button"
                              onClick={handleUnselectMapDevice}
                              sx={{
                                ...publicWifiMapActionBtnSx,
                                mt: 0,
                                bgcolor: 'transparent',
                                color: MAP_UI.accent,
                                '&:hover': { bgcolor: MAP_UI.accentTintHover },
                              }}
                            >
                              Unselect
                            </Box>
                          </Tooltip>
                        )}
                        <Box
                          component="button"
                          onClick={() => {
                            setSelectedDevice(null);
                            goToDevice(
                              selectedDevice.device?.serial || selectedDevice.device?.serial_number
                            );
                          }}
                          sx={{ ...publicWifiMapActionBtnSx, mt: 0 }}
                        >
                          View Device
                        </Box>
                      </Stack>
                    </Box>
                  </InfoWindow>
                )}
              </GoogleMap>
              <Box
                component="aside"
                aria-label="Map marker legend"
                sx={{
                  position: 'absolute',
                  top: { xs: 8, sm: 12 },
                  left: { xs: 8, sm: 12 },
                  zIndex: 2,
                  maxWidth: { xs: 260, sm: 288 },
                  p: 1.25,
                  borderRadius: 1,
                  bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(30,30,30,0.92)' : 'rgba(255,255,255,0.94)'),
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 2,
                  pointerEvents: 'none',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.75, letterSpacing: 0.2 }}>
                  Marker legend
                </Typography>
                {mapLegendRows.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No devices selected on map
                  </Typography>
                ) : (
                  mapLegendRows.map((row, idx, arr) => (
                    <Box
                      key={row.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        mb: idx < arr.length - 1 ? 0.6 : 0,
                      }}
                    >
                      <Box
                        sx={{
                          flexShrink: 0,
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          bgcolor: row.color,
                          border: '2px solid #fff',
                          color: '#fff',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontFamily: 'Arial, sans-serif',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                          lineHeight: 1,
                        }}
                      >
                        {row.label}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35, pt: 0.15 }}>
                        {row.text}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
              </>
            )}
          </Box>
        </Paper>
      )}

      {/* Planned APs + enrichment tables (same structure in normal and deployment plan mode) */}
      {rrmData && (
        <Paper elevation={2} sx={{ p: 3 }}>
          {!deploymentPlanMode && conflictingExcluded.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Operational Conflict: {conflictingExcluded.length} critical parent NODE(s) have 5 GHz Public WiFi active
              </Typography>
              <Typography variant="body2">
                The following NODEs are critical tree management parents and must NOT run 5 GHz Public WiFi,
                but currently do: <strong>{conflictingExcluded.map(e => e.serial).join(', ')}</strong>.
                Remove the 5 GHz SSID assignment from these devices.
              </Typography>
            </Alert>
          )}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              The minimal plan lists APs that cover the area efficiently. The enrichment list shows other strong candidates
              you can add for denser coverage or hotspot overlap.
            </Typography>
          </Box>

          {plannedApRows.length === 0 && tableRecommendations.length === 0 ? (
            <Alert severity="info">
              No eligible NODE devices found, or all are hidden by the current filter.
              {hideLowScore && ' Try disabling "Show only recommended candidates" to see all results.'}
              {backhaulFilterActive && ' Try clearing the iperf table filter in Configuration.'}
            </Alert>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap', mb: 0.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Planned APs for 5 GHz Public WiFi
                </Typography>
                {rrmData?.computed_at && (
                  <Typography variant="caption" color="text.secondary">
                    Plan from {new Date(rrmData.computed_at).toLocaleString()}
                  </Typography>
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Minimal area-covering set from the stored deployment plan (AP count kept small while covering eligible NODEs).
              </Typography>
              {plannedApRows.length === 0 ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                  No deployment plan is stored yet. Open deployment plan mode to generate a plan, or wait for the site to sync from the server.
                </Alert>
              ) : (
                <>
                <TableContainer sx={{ mb: 1 }}>
                  <Table size="small">
                    {rrmNormalModeTableHead}
                    <TableBody>
                      {sortedFilteredPlannedApRows.map(({ planEntry, cand }) => {
                        const serial = planEntry.serial;
                        const isExpanded = !!expandedRows[serial];
                        const candSerialLower = (serial || '').toLowerCase();
                        const candHasPW = publicWifiAnySerials.has(candSerialLower);
                        const candHas5G = publicWifi5GHzSerials.has(candSerialLower);
                        const { upMbps, downMbps } = getCandidateIperfMbps(serial);
                        const minUp   = Number(minBackhaulUpMbps);
                        const minDown = Number(minBackhaulDownMbps);
                        const upBelowThreshold   = backhaulFilterActive && minBackhaulUpMbps   !== '' && !isNaN(minUp)   && minUp   > 0 && (upMbps   == null || upMbps   < minUp);
                        const downBelowThreshold = backhaulFilterActive && minBackhaulDownMbps !== '' && !isNaN(minDown) && minDown > 0 && (downMbps == null || downMbps < minDown);
                        return (
                          <React.Fragment key={serial}>
                            <TableRow hover sx={{ cursor: 'pointer' }}>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  size="small"
                                  checked={selectedPlannedSerials.has(serial)}
                                  onChange={() => togglePlannedSerial(serial)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    setExpandedRows(prev => ({ ...prev, [serial]: !prev[serial] }));
                                  }}
                                >
                                  {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                </IconButton>
                              </TableCell>
                              <TableCell onClick={() => goToDevice(serial)}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.95rem' }}>
                                    {serial}
                                  </Typography>
                                  {candHasPW && (
                                    <Tooltip title={candHas5G ? 'Public WiFi enabled (including 5 GHz)' : 'Public WiFi enabled'}>
                                      <Chip
                                        label={candHas5G ? 'W 5G ✓' : 'W ✓'}
                                        size="small"
                                        sx={rrmPlanChipSx}
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={planEntry.covered_node_count}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontWeight: 600, ...rrmPlanChipSx }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                {cand ? (
                                  <Tooltip title={`Score: ${cand.score_5ghz.toFixed(1)}/100 · Classification: ${cand.classification}`}>
                                    <Chip
                                      label={cand.score_5ghz.toFixed(0)}
                                      size="small"
                                      sx={getScoreChipSx(cand.score_5ghz)}
                                    />
                                  </Tooltip>
                                ) : '—'}
                              </TableCell>
                              <TableCell align="right">
                                {cand?.latency_ms != null ? formatLatencyMs(cand.latency_ms) : '—'}
                              </TableCell>
                              <TableCell align="right">
                                {cand?.tx_failed_pct != null ? `${(cand.tx_failed_pct * 100).toFixed(1)}%` : '—'}
                              </TableCell>
                              <TableCell align="right">
                                {cand?.backhaul_score != null ? cand.backhaul_score.toFixed(1) : '—'}
                              </TableCell>
                              <TableCell align="right" sx={upBelowThreshold ? { color: 'warning.main', fontWeight: 600 } : {}}>
                                {upMbps != null ? upMbps.toFixed(0) : 'N/A'}
                              </TableCell>
                              <TableCell align="right" sx={downBelowThreshold ? { color: 'warning.main', fontWeight: 600 } : {}}>
                                {downMbps != null ? downMbps.toFixed(0) : 'N/A'}
                              </TableCell>
                              <TableCell>
                                {cand ? (
                                  <Chip
                                    label={getRecommendationLabel(cand.recommendation)}
                                    size="small"
                                    sx={getRecommendationChipSx(cand.recommendation)}
                                  />
                                ) : '—'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={11} sx={{ p: 0, border: 0 }}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ px: 3, py: 2, bgcolor: 'action.hover' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                      Why recommended:
                                    </Typography>
                                    {cand?.reasons?.length > 0 ? (
                                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                        {cand.reasons.map((reason, idx) => (
                                          <Typography
                                            key={idx}
                                            component="li"
                                            variant="body2"
                                            sx={{ mb: 0.5, color: 'text.secondary' }}
                                          >
                                            {reason}
                                          </Typography>
                                        ))}
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">
                                        {cand ? 'No specific reasons available.' : 'No RRM candidate record for this serial — plan entry only.'}
                                      </Typography>
                                    )}
                                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                      {cand?.classification && (
                                        <Chip label={`Classification: ${cand.classification}`} size="small" variant="outlined" />
                                      )}
                                      {(cand?.child_count ?? 0) > 0 && (
                                        <Chip
                                          label={`Children: ${cand.child_count}`}
                                          size="small"
                                          sx={healthChipSoftSx('warning')}
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                {selectedPlannedSerials.size > 0 && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, mb: 2 }}>
                    <Chip label={`${selectedPlannedSerials.size} selected`} size="small" variant="outlined" sx={rrmPlanChipSx} />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<GroupIcon />}
                      endIcon={<ArrowDropDownIcon />}
                      onClick={(e) => { setGroupMenuSource('planned'); setGroupMenuAnchor(e.currentTarget); }}
                    >
                      Group
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<WifiIcon />} onClick={() => openActionDialog('planned', 'assign_direct')}>
                      Assign SSID
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<SettingsInputAntennaIcon />} onClick={() => openActionDialog('planned', 'assign_radio')}>
                      Assign Radio Profile
                    </Button>
                  </Stack>
                )}
                </>
              )}

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Additional AP candidates (enrichment)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Scored NODEs not selected in the minimal plan. Use these to add capacity, improve density, or fill gaps beyond the baseline.
              </Typography>
              {tableRecommendations.length === 0 ? (
                <Alert severity="info">
                  No RRM candidates in the current view (empty analysis or all hidden by the filter).
                  {hideLowScore && ' Try disabling "Show only recommended candidates".'}
                </Alert>
              ) : enrichmentCandidates.length === 0 ? (
                <Alert severity="info">
                  Every candidate that passes the current filter is already a planned AP in the minimal set.
                </Alert>
              ) : (
                <>
                <TableContainer>
                  <Table size="small">
                    {rrmEnrichmentTableHead}
                    <TableBody>
                      {sortedFilteredEnrichmentCandidates.map((cand) => {
                        const isExpanded = !!expandedRows[cand.serial];
                        const candSerialLower = (cand.serial || '').toLowerCase();
                        const candHasPW = publicWifiAnySerials.has(candSerialLower);
                        const candHas5G = publicWifi5GHzSerials.has(candSerialLower);
                        const eUpMbps = typeof cand.iperf_up_mbps === 'number' ? cand.iperf_up_mbps : null;
                        const eDownMbps = typeof cand.iperf_down_mbps === 'number' ? cand.iperf_down_mbps : null;
                        const eMinUp   = Number(minBackhaulUpMbps);
                        const eMinDown = Number(minBackhaulDownMbps);
                        const eUpBelow   = backhaulFilterActive && minBackhaulUpMbps   !== '' && !isNaN(eMinUp)   && eMinUp   > 0 && (eUpMbps   == null || eUpMbps   < eMinUp);
                        const eDownBelow = backhaulFilterActive && minBackhaulDownMbps !== '' && !isNaN(eMinDown) && eMinDown > 0 && (eDownMbps == null || eDownMbps < eMinDown);
                        return (
                          <React.Fragment key={cand.serial}>
                            <TableRow hover sx={{ cursor: 'pointer' }}>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  size="small"
                                  checked={selectedEnrichmentSerials.has(cand.serial)}
                                  onChange={() => toggleEnrichmentSerial(cand.serial)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    setExpandedRows(prev => ({ ...prev, [cand.serial]: !prev[cand.serial] }));
                                  }}
                                >
                                  {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                </IconButton>
                              </TableCell>
                              <TableCell onClick={() => goToDevice(cand.serial)}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.95rem' }}>
                                    {cand.serial}
                                  </Typography>
                                  {candHasPW && (
                                    <Tooltip title={candHas5G ? 'Public WiFi enabled (including 5 GHz)' : 'Public WiFi enabled'}>
                                      <Chip
                                        label={candHas5G ? 'W 5G ✓' : 'W ✓'}
                                        size="small"
                                        sx={rrmPlanChipSx}
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                {cand.nearest_planned_ap_m != null && cand.nearest_planned_ap_m >= 0 ? (
                                  <Tooltip title="Approximate distance to the nearest AP in the minimal plan">
                                    <Chip
                                      label={`${cand.nearest_planned_ap_m.toFixed(0)} m`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontWeight: 600 }}
                                    />
                                  </Tooltip>
                                ) : '—'}
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title={`Score: ${cand.score_5ghz.toFixed(1)}/100 · Classification: ${cand.classification}`}>
                                  <Chip
                                    label={cand.score_5ghz.toFixed(0)}
                                    size="small"
                                    sx={getScoreChipSx(cand.score_5ghz)}
                                  />
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right">
                                {cand.latency_ms != null ? formatLatencyMs(cand.latency_ms) : '—'}
                              </TableCell>
                              <TableCell align="right">
                                {cand.tx_failed_pct != null ? `${(cand.tx_failed_pct * 100).toFixed(1)}%` : '—'}
                              </TableCell>
                              <TableCell align="right">
                                {cand.backhaul_score != null ? cand.backhaul_score.toFixed(1) : '—'}
                              </TableCell>
                              <TableCell align="right" sx={eUpBelow ? { color: 'warning.main', fontWeight: 600 } : {}}>
                                {eUpMbps != null ? eUpMbps.toFixed(0) : 'N/A'}
                              </TableCell>
                              <TableCell align="right" sx={eDownBelow ? { color: 'warning.main', fontWeight: 600 } : {}}>
                                {eDownMbps != null ? eDownMbps.toFixed(0) : 'N/A'}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={getRecommendationLabel(cand.recommendation)}
                                  size="small"
                                  sx={getRecommendationChipSx(cand.recommendation)}
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={11} sx={{ p: 0, border: 0 }}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ px: 3, py: 2, bgcolor: 'action.hover' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                      Why recommended:
                                    </Typography>
                                    {cand.reasons?.length > 0 ? (
                                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                        {cand.reasons.map((reason, idx) => (
                                          <Typography
                                            key={idx}
                                            component="li"
                                            variant="body2"
                                            sx={{ mb: 0.5, color: 'text.secondary' }}
                                          >
                                            {reason}
                                          </Typography>
                                        ))}
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">
                                        No specific reasons available.
                                      </Typography>
                                    )}
                                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                      <Chip label={`Classification: ${cand.classification}`} size="small" variant="outlined" />
                                      {cand.child_count > 0 && (
                                        <Chip
                                          label={`Children: ${cand.child_count}`}
                                          size="small"
                                          sx={healthChipSoftSx('warning')}
                                        />
                                      )}
                                      {cand.nearest_planned_ap_m != null && cand.nearest_planned_ap_m >= 0 && (
                                        <Chip
                                          label={`Nearest planned AP: ${cand.nearest_planned_ap_m.toFixed(0)}m`}
                                          size="small"
                                          variant="outlined"
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                {selectedEnrichmentSerials.size > 0 && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, mb: 1 }}>
                    <Chip label={`${selectedEnrichmentSerials.size} selected`} size="small" variant="outlined" sx={rrmPlanChipSx} />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<GroupIcon />}
                      endIcon={<ArrowDropDownIcon />}
                      onClick={(e) => { setGroupMenuSource('enrichment'); setGroupMenuAnchor(e.currentTarget); }}
                    >
                      Group
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<WifiIcon />} onClick={() => openActionDialog('enrichment', 'assign_direct')}>
                      Assign SSID
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<SettingsInputAntennaIcon />} onClick={() => openActionDialog('enrichment', 'assign_radio')}>
                      Assign Radio Profile
                    </Button>
                  </Stack>
                )}
                </>
              )}
            </>
          )}
        </Paper>
      )}

      {/* Group dropdown menu */}
      <Menu anchorEl={groupMenuAnchor} open={Boolean(groupMenuAnchor)} onClose={() => setGroupMenuAnchor(null)}>
        <MenuItem onClick={() => openActionDialog(groupMenuSource, 'create_group')}>
          <ListItemIcon><GroupAddIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Create Group</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => openActionDialog(groupMenuSource, 'add_to_group')}>
          <ListItemIcon><AddCircleOutlineIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Add to Existing Group</ListItemText>
        </MenuItem>
      </Menu>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => { if (!actionSaving) closeActionDialog(); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionMode === 'create_group' && 'Create Group from Selected Devices'}
          {actionMode === 'add_to_group' && 'Add Selected Devices to Group'}
          {actionMode === 'assign_direct' && 'Assign SSID to Selected Devices'}
          {actionMode === 'assign_radio' && 'Assign Radio Profile to Selected Devices'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {actionDialogSource === 'planned'
              ? `${selectedPlannedSerials.size} device(s) selected from Planned APs`
              : `${selectedEnrichmentSerials.size} device(s) selected from Enrichment Candidates`}
          </Typography>

          {actionMode === 'create_group' && (
            <TextField
              label="Group Name"
              fullWidth
              value={actionGroupName}
              onChange={(e) => setActionGroupName(e.target.value)}
              sx={{ mb: 2 }}
              autoFocus
            />
          )}

          {actionMode === 'add_to_group' && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="action-group-label">Select Group</InputLabel>
              <Select
                labelId="action-group-label"
                label="Select Group"
                value={actionGroupId}
                onChange={(e) => setActionGroupId(e.target.value)}
                autoFocus
              >
                <MenuItem value=""><em>Choose a group…</em></MenuItem>
                {groups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>{g.name || g.id}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {(actionMode === 'create_group' || actionMode === 'assign_direct') && (
            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel id="action-ssid-label">SSID Profile {actionMode === 'create_group' ? '(optional)' : ''}</InputLabel>
              <Select
                labelId="action-ssid-label"
                label={`SSID Profile ${actionMode === 'create_group' ? '(optional)' : ''}`}
                value={actionSsidId}
                onChange={(e) => setActionSsidId(e.target.value)}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {ssidProfiles.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {formatSsidProfileOptionLabel(p, vlanProfiles)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {actionMode === 'assign_radio' && (
            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel id="action-radio-label">Radio Profile</InputLabel>
              <Select
                labelId="action-radio-label"
                label="Radio Profile"
                value={actionRadioProfileId}
                onChange={(e) => setActionRadioProfileId(e.target.value)}
                autoFocus
              >
                <MenuItem value=""><em>Choose a radio profile…</em></MenuItem>
                {radioProfiles.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {formatRadioProfileOptionLabel(p)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          {/* Cancel both closes the dialog AND aborts any in-flight bulk
              request via the shared AbortController. Without abort, fetch
              keeps running and a late snackbar can appear after the user
              has navigated away. */}
          <Button onClick={closeActionDialog}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              !canMutate ||
              actionSaving ||
              (actionMode === 'create_group' && !actionGroupName.trim()) ||
              (actionMode === 'add_to_group' && !actionGroupId) ||
              (actionMode === 'assign_direct' && !actionSsidId) ||
              (actionMode === 'assign_radio' && !actionRadioProfileId)
            }
            onClick={
              actionMode === 'create_group' ? handleCreateGroupAction :
              actionMode === 'add_to_group' ? handleAddToGroupAction :
              actionMode === 'assign_radio' ? handleAssignRadioProfileAction :
              handleAssignSSIDAction
            }
          >
            {actionSaving ? <CircularProgress size={20} /> :
              actionMode === 'create_group' ? 'Create' :
              actionMode === 'add_to_group' ? 'Add to Group' :
              'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default PublicWifiExpansion;
